<template>
  <vddl-draggable
    :key="item.id"
    :draggable="item"
    :index="index"
    :wrapper="list"
    :dragstart="onDragStart"
    :dragend="onDragEnd"
    :moved="onMove"
    :canceled="onCancel"
    :type="type"
    :disable-if="!canEdit"
  >
    <VTooltip
      placement="top"
      :triggers="['hover']"
      popper-class="v-popper--compCollection-item"
      :delay="delayOptions"
      :disabled="popoverDisabled"
      distance="3"
      @apply-hide="delayOptions.hide = 100"
    >
      <div
        v-close-popper.all
        class="comp tooltip-target b3"
        :class="{ nobackground: imageUrl }"
        :draggable="!!canEdit"
        @drag="onComponentDrag"
        @dragstart="onComponentStartDrag($event, item)"
        @mousedown="onMousedownComp"
        @mouseup="onMouseupComp"
        @click="onClickComp($event, item)"
        @contextmenu.prevent="onContextMenu"
      >
        <img v-if="imageUrl" :src="imageUrl" @load="onImageLoad" />
        <svg-icon
          v-else-if="icon"
          :icon="icon"
          :width="'36'"
          :height="'36'"
          @on-load="onIconLoad"
        />
      </div>
      <template #popper>
        <div v-if="imageUrl" class="thumb" :class="{ nobackground: imageUrl }">
          <img :src="imageUrl" />
        </div>
        <div class="details">
          <div class="name">
            {{ item.name }}
          </div>
          <small v-if="componentParent !== getRootContainer" class="bottom">
            {{ componentParent.name }}
          </small>
        </div>
      </template>
    </VTooltip>
  </vddl-draggable>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, EVENT, DRAG_TYPE, DRAG_LIST } from '@/constants.js';
import SvgIcon from '../../all/SvgIcon.vue';
import iconMetadata from '../../../vuex/icon-metadata.js';
import { loadImage, has } from '../../../utils.js';
import { round2DContextPath } from '../../../canvas-utils.js';

export default {
  name: 'List',

  components: {
    SvgIcon,
  },

  props: {
    item: {
      type: Object,
      default: null,
    },
    list: {
      type: Array,
      default: () => [],
    },
    // The index of this item in the component list
    index: {
      type: Number,
      default: null,
    },
    // The id of the container of the component list
    id: {
      type: String,
      default: null,
    },
    // The type of the dragged item
    type: {
      type: String,
      default: null,
    },
    // A callback when an item is moved from this list
    moveAction: {
      type: Function,
      default: () => {},
    },
    // A callback when an item is removed from this list
    deleteAction: {
      type: Function,
      default: () => {},
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      dragCanvas: null,
      delayOptions: { show: 800, hide: 100 },
    };
  },

  computed: {
    ...mapGetters('project/data/components', [
      'getRootContainer',
      'getContainedNodes',
      'getDefaultIcon',
      'getContainerNode',
    ]),
    ...mapGetters('project/config/components', [
      'getSelections',
      'getIsCollapsed',
    ]),
    ...mapGetters('project/config/components', { getOpenComponent: 'getOpen' }),
    ...mapGetters('project/config/boards', ['getTransform', 'getOpen']),
    ...mapGetters('application/drag', [
      'getIsEmpty',
      'getItems',
      'getSource',
      'getTarget',
    ]),
    ...mapGetters('application/appStates', ['getIsProjectLoaded']),
    ...mapGetters('project/data/assets', ['getSmallImageUrl']),

    /**
     * Return the parent component of the current component
     * @return {Object}
     */
    componentParent() {
      return this.getContainerNode(this.item.id, 'components', 'children');
    },

    /**
     * Return the cover asset id of the component
     * @return {String|null}
     */
    coverId() {
      return this.item.assets?.cover?.id;
    },

    /**
     * Return the small image url of the cover asset
     * @return {String|null}
     */
    imageUrl() {
      return this.getSmallImageUrl(this.coverId);
    },

    /**
     * Return the icon of the cover asset (or the default icon)
     * @return {String|null}
     */
    icon() {
      if (this.item.assets?.cover?.type === 'icon') {
        return this.item.assets?.cover?.file;
      }
      return this.getDefaultIcon;
    },

    /**
     * Return whether the popover is disabled
     * @return {Boolean}
     */
    popoverDisabled() {
      if (!this.getIsEmpty) {
        return true;
      }
      if (this.type === 'ELEMENT_COMPONENT' && this.showModal) {
        return true;
      }
      return false;
    },

    /**
     * Return the current open board id
     * @return {String}
     */
    boardId() {
      return this.getOpen;
    },

    /**
     * Return the current board transform
     * @returns {object}
     */
    transform() {
      return this.getTransform(this.boardId);
    },

    showModal() {
      return this.getIsProjectLoaded && this.getOpenComponent !== null;
    },
  },

  methods: {
    ...mapActions('project/config/components', ['setOpen']),
    ...mapActions('project/data/elements', {
      moveElementComponent: 'moveComponent',
      deleteElementComponent: 'deleteComponent',
    }),

    ...mapActions('project/data/attributes', {
      moveAttributeComponent: 'moveComponent',
      deleteAttributeComponent: 'deleteComponent',
    }),
    ...mapActions('application/drag', ['setItems', 'setSource', 'clear']),
    ...mapActions('application/contextMenu', [
      'createItem',
      'setCalcPosition',
      'deleteMenu',
    ]),

    /**
     * Custom event from svg icon load
     */
    async onIconLoad(svg) {
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      this.assetImage = await loadImage(url);
    },

    /**
     * Image onLoad event
     */
    onImageLoad() {
      this.assetImage = new Image();
      this.assetImage.src = this.imageUrl;
    },

    onMousedownComp(event) {
      this.$emit('disableDrag', event);
    },

    onMouseupComp(event) {
      this.$emit('enableDrag', event);
    },

    onComponentDrag($event) {
      emitter.emit(EVENT.DRAGGING_COMPONENT, $event);
    },

    /**
     * Event handler for vddl dragstart event
     */
    onDragStart(event) {
      // WARNING! custom vddl-drag (changed draggable to event)

      // Store source drag information
      this.setItems({
        value: {
          type: this.type,
          ids: [this.item.id],
        },
      });
      this.setSource({
        value: {
          type: DRAG_LIST.COMPONENTS,
          ids: [this.id],
          indexes: [this.index],
        },
      });
    },

    /**
     * Event handler for hmtl 5 dragstart
     */
    onComponentStartDrag($event, item) {
      if (!this.canEdit) return;

      const { scale } = this.transform;

      // Create a canvas element for image manipulation
      const canvas = document.createElement('canvas');
      canvas.classList.add('dragCanvas');
      canvas.width = 60 * scale;
      canvas.height = 60 * scale;

      // This is required in Chrome (check styles below)
      document.body.append(canvas);
      const ctx = canvas.getContext('2d');

      ctx.fillStyle = '#222';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Clip a rounded rectangle
      round2DContextPath(ctx, 0, 0, canvas.width, canvas.height, 4 * scale);
      ctx.clip();

      if (this.imageUrl) {
        const image = this.assetImage;

        // Compute the minimum dimension in order to crop the image
        const minDimension =
          image.width < image.height ? image.width : image.height;

        // Offsets for image cropping
        const sx = (image.width - minDimension) / 2;
        const sy = (image.height - minDimension) / 2;

        // Draw the image on the canvas
        ctx.drawImage(
          image,
          sx,
          sy,
          minDimension,
          minDimension,
          0,
          0,
          canvas.width,
          canvas.height
        );
      } else if (this.icon) {
        const offset = ((60 - 36) / 2) * scale;

        // Draw the image on the canvas
        ctx.drawImage(
          this.assetImage,
          offset,
          offset,
          canvas.width - 2 * offset,
          canvas.height - 2 * offset
        );
      }

      // Draw a rounded rectangle around the image
      ctx.globalCompositeOperation = 'source-over';
      round2DContextPath(ctx, 0, 0, canvas.width, canvas.height, 4 * scale);
      ctx.lineWidth = 5 * scale;
      ctx.strokeStyle = '#db841e';
      ctx.stroke();

      $event.dataTransfer.setDragImage(
        canvas,
        canvas.width / 2,
        canvas.height / 2
      );
    },

    /**
     * Event handler for vddl dragend event.
     */
    onDragEnd(event) {
      // Remove any drag canvas
      $('.dragCanvas').remove();
    },

    onClickComp(event, item) {
      // Set popover hiding delay to 0
      this.delayOptions.hide = 0;

      this.setOpen({ id: item.id, apply: true });

      // Enable element dragging (disabled by mousedown)
      this.$emit('enableDrag', event);
    },

    /**
     * Event handler for vddl moved event. Triggered after the component has completed moving.
     */
    onMove(event) {
      // Call the move callback
      this.moveAction();
    },

    /**
     * Event listener - contextmenu event.
     */
    onContextMenu(event) {
      if (this.canEdit) {
        // Show the context menu
        this.createContextMenu(event.clientX, event.clientY);
      }
    },

    /**
     * Create a context menu for this element
     * @param {Number} x  The x client coordinate
     * @param {Number} y  The y client coordinate
     */
    async createContextMenu(x, y) {
      const source = this.getSource;
      this.createItem({
        name: 'Remove reference',
        icon: null,
        commands: {
          doAction: () => {
            // Call the delete callback, passing the container id and the component index
            this.deleteAction(this.id, this.index);
            this.deleteMenu();
          },
          isActive: () => {
            return true;
          },
          isEnabled: () => {
            return true;
          },
        },
        submenu: null,
      });

      this.setCalcPosition({
        calcPosition: () => {
          return { left: x, top: y };
        },
      });
    },

    /**
     * Event handler for vddl canceled event. The drag has been canceled.
     */
    onCancel(event) {
      this.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
.attribute {
  .comp {
    margin: 0;
  }
}
.comp {
  height: 64px;
  width: 64px;
  background: #1d1f1f;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid #2c3233;
  cursor: pointer;
  transition: border 0.25s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border: 2px solid #db841e;
  }
  &.nobackground {
    background: #2c3233;
  }
}

.comp img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.comp.selected {
  border: 2px solid #db841e;
}
</style>
<style lang="scss">
.dragCanvas {
  position: absolute;
  left: -50000px;
}
.v-popper__popper.v-popper--compCollection-item {
  .v-popper__inner {
    background: #2c3233;
    color: #bcbcbc;
    padding: 10px;
    border-radius: 3px;
    box-shadow: 0 1px 10px rgba(0, 0, 0, 0.5);
    display: flex;

    > div {
      display: flex;
    }

    &.no-padding {
      padding: 0;
    }
  }

  .thumb {
    background: url(/images/component.svg?3606044…) #485050;
    background-repeat: no-repeat;
    background-size: 44px;
    background-position: center;
    border-radius: 3px;
    max-width: 100px;
    width: 100px;
    max-height: 100px;
    height: 100px;
    overflow: hidden;
    margin: 0 10px 0 0;

    &.nobackground {
      background: #485050;
    }

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  .name {
    padding: 0 0 5px 0;
    color: #fff;
    font-size: 16px;
    font-weight: 100;
  }

  small {
    text-transform: uppercase;
    display: block;
    padding: 0;
    opacity: 0.5;

    &.bottom {
      padding: 0;
    }
  }
}
</style>
